import { useState, useEffect } from "react";
import CategoriesComponent from "../Categories/CategoriesComponent";
import Pagination from "@mui/material/Pagination";
import React from "react";
import { useShoppingCart } from "../context/ShoppingCartContext";
import type { Product } from "../interfaces/catalogProduct";
import { useAppSelector } from "../../../store/hooks";
import ProductComponent from "../Products/ProductComponent";
import ProductsSortComponent from "../Products/ProductsSortComponent";
import { ProductsServicesComponent as ProductsServicesComponent_ } from "../interfaces/base";
import "./ProductsServicesComponent.css";
import ProductServicesBuyComponent from "./ProductServicesBuyComponent";
import usePageLanguage from "../hooks/usePageLanguage";
import { matchesEditorTarget } from "../utilities/componentIdentity";
import { resolveCatalogProductPricing } from "../utilities/catalogProductAdapter";
import { getVariantPriceRange } from "../utilities/productCatalog";

import styled from "@emotion/styled";
import { useSearchParams } from "react-router-dom";
import { ProductCategory } from "../context/ShoppingCartContext";

const Div = styled("div")(
  null,
  (props) =>
    function (props: any) {
      return { ...props["data-style"] };
    }
);

export default function ProductServicesComponent({
  id,
  uuid,
  attributes,
}: ProductsServicesComponent_) {
  const editorId = useAppSelector((state) => state.textEditor?.get("id"));
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category");

  const { listableCatalogProducts, pricingNow } = useShoppingCart();

  const getSortPrice = (product: Product) => {
    const range = getVariantPriceRange(product);
    if (range) {
      return range.minPrice;
    }
    return resolveCatalogProductPricing(product, pricingNow).price_total;
  };

  const [productCategoryUuid, setProductCategoryUuid] = useState<string | null>(
    categoryParam
  );
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(30);
  const [sortFilter, setSortFilter] = useState<string>("");
  const lang = usePageLanguage();
  const handlePageChange = (event: any, value: number) => {
    setPage(value);
  };

  const updateSearchParams = (
    updater: (nextParams: URLSearchParams) => void
  ) => {
    const nextParams = new URLSearchParams(searchParams);
    updater(nextParams);

    const currentSerialized = searchParams.toString();
    const nextSerialized = nextParams.toString();
    if (currentSerialized !== nextSerialized) {
      setSearchParams(nextParams, { replace: true });
    }
  };

  const clearSearchParams = () =>
    updateSearchParams((nextParams) => {
      Array.from(nextParams.keys()).forEach((key) => nextParams.delete(key));
    });

  useEffect(() => {
    if (!categoryParam) {
      setProductCategoryUuid(null);
      setPage(1);
    }
  }, [categoryParam]);

  const filteredProducts = listableCatalogProducts.filter((product) => {
    if (productCategoryUuid) {
      return Array.isArray(product.productCategories) && product.productCategories.some((productCategory: any) => {
        // Defensive: handle both ProductCategory and possible lighter objects
        return (
          productCategory &&
          typeof productCategory === "object" &&
          "uuid" in productCategory &&
          productCategory.uuid === productCategoryUuid
        );
      });
    }
    return true;
  });

  const matchingProducts = [...filteredProducts];

  switch (sortFilter) {
    case "price_asc":
      matchingProducts.sort((a, b) => getSortPrice(a) - getSortPrice(b));
      break;
    case "price_desc":
      matchingProducts.sort((a, b) => getSortPrice(b) - getSortPrice(a));
      break;
    case "name_asc":
      matchingProducts.sort((a, b) => {
        const nameA = (a.translations?.[lang]?.name || "").toUpperCase();
        const nameB = (b.translations?.[lang]?.name || "").toUpperCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
      break;
    case "name_desc":
      matchingProducts.sort((a, b) => {
        const nameA = (a.translations?.[lang]?.name || "").toUpperCase();
        const nameB = (b.translations?.[lang]?.name || "").toUpperCase();
        if (nameA < nameB) {
          return 1;
        }
        if (nameA > nameB) {
          return -1;
        }
        return 0;
      });
      break;

    default:
      break;
  }

  const startOffset = (page - 1) * perPage;
  const endOffset = startOffset + perPage;

  const pageProducts = matchingProducts.slice(startOffset, endOffset);
  const pageCount = Math.ceil(matchingProducts.length / perPage);

  const changeCategoryUuid = function (categoryUuid: null | string) {
    if (categoryUuid) {
      updateSearchParams((nextParams) => {
        nextParams.set("category", categoryUuid);
      });
    } else {
      clearSearchParams();
    }
    setProductCategoryUuid(categoryUuid);
    setPage(1);
  };

  return (
    <Div
      id={id.toString()}
      key={id}
      data-style={attributes && attributes.styles}
      className={
        attributes &&
        attributes.classes +
          (attributes.classes != "container" &&
          attributes.classes != "container-fluid"
            ? " builder-component builder-component__products"
            : "") +
          (matchesEditorTarget({ id, uuid }, editorId) ? " builder-component--active" : "")
      }
      data-component-type="section"
    >
      <div className="products_container">
        <div className="row" style={{ position: "relative" }}>
          <div className="col-lg-3 sticky">
            <CategoriesComponent
              id={id}
              productCategoryUuid={productCategoryUuid}
              setProductCategoryUuid={changeCategoryUuid}
            />

            <ProductServicesBuyComponent />
          </div>
          <div className="col-lg-9">
            <ProductsSortComponent
              sortFilter={sortFilter}
              setSortFilter={setSortFilter}
              perPage={perPage}
              setPerPage={setPerPage}
            />
            {/*@include('webstore.filters')*/}
            <div className="card itprCard" id="productsPanel">
              <div className="card-body">
                {pageProducts.map((product: Product) => {
                  return (
                    <ProductComponent
                      key={product.productUuid}
                      product={product}
                    />
                  );
                })}
              </div>
              {pageCount > 1 && (
                <div className="card-footer">
                  <Pagination
                    className="MuiPagination-ul-center "
                    shape="rounded"
                    count={pageCount}
                    page={page}
                    onChange={handlePageChange}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Div>
  );
}
